/**
 * Security Matrix Constants
 * Defines permissions, features, and roles for the Planned Maintenance System
 *
 * Permission format from API: "feature.action" (e.g., "providers.add", "workorders.view")
 */

// ===========================================
// PERMISSION ACTIONS
// ===========================================

/**
 * Available permission actions
 * These match the action suffix in the API permission strings
 */
export const PERMISSION_ACTIONS = {
  ADD: 'add',
  EDIT: 'edit',
  VIEW: 'view',
  DELETE: 'delete',
} as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[keyof typeof PERMISSION_ACTIONS];

// ===========================================
// FEATURES
// ===========================================

/**
 * Available features in the application
 * These match the feature prefix in the API permission strings
 */
export const FEATURES = {
  PROVIDERS: 'providers',
  PM_SERVICES: 'pmservices',
  WORK_ORDERS: 'workorders',
  PROGRAM_ASSIGNMENT: 'programassignment',
  FREQUENCY_CONFIGURATION: 'frequencyconfiguration',
  SERVICE_DEFINITION: 'servicedefinition',
  ACTION_CENTRE: 'actioncentre',
  OVERVIEW: 'overview',
  SCENARIOS: 'scenarios',
  LOCATIONS: 'locations',
  OPENING_AND_CLOSING: 'openingandclosing',
  ACCESS_CONTROL: 'accesscontrol',
  USER_MANAGEMENT: 'usermanagement',
} as const;

export type Feature = (typeof FEATURES)[keyof typeof FEATURES];

// ===========================================
// PERMISSION STRINGS
// ===========================================

/**
 * All possible permission strings in the system
 * Format: "feature.action"
 */
export const PERMISSIONS = {
  // Provider Management
  PROVIDERS_ADD: `${FEATURES.PROVIDERS}.${PERMISSION_ACTIONS.ADD}`,
  PROVIDERS_EDIT: `${FEATURES.PROVIDERS}.${PERMISSION_ACTIONS.EDIT}`,
  PROVIDERS_VIEW: `${FEATURES.PROVIDERS}.${PERMISSION_ACTIONS.VIEW}`,
  PROVIDERS_DELETE: `${FEATURES.PROVIDERS}.${PERMISSION_ACTIONS.DELETE}`,

  // PM Services
  PM_SERVICES_ADD: `${FEATURES.PM_SERVICES}.${PERMISSION_ACTIONS.ADD}`,
  PM_SERVICES_EDIT: `${FEATURES.PM_SERVICES}.${PERMISSION_ACTIONS.EDIT}`,
  PM_SERVICES_VIEW: `${FEATURES.PM_SERVICES}.${PERMISSION_ACTIONS.VIEW}`,
  PM_SERVICES_DELETE: `${FEATURES.PM_SERVICES}.${PERMISSION_ACTIONS.DELETE}`,

  // Work Orders
  WORK_ORDERS_ADD: `${FEATURES.WORK_ORDERS}.${PERMISSION_ACTIONS.ADD}`,
  WORK_ORDERS_EDIT: `${FEATURES.WORK_ORDERS}.${PERMISSION_ACTIONS.EDIT}`,
  WORK_ORDERS_VIEW: `${FEATURES.WORK_ORDERS}.${PERMISSION_ACTIONS.VIEW}`,
  WORK_ORDERS_DELETE: `${FEATURES.WORK_ORDERS}.${PERMISSION_ACTIONS.DELETE}`,

  // Program Assignment
  PROGRAM_ASSIGNMENT_ADD: `${FEATURES.PROGRAM_ASSIGNMENT}.${PERMISSION_ACTIONS.ADD}`,
  PROGRAM_ASSIGNMENT_EDIT: `${FEATURES.PROGRAM_ASSIGNMENT}.${PERMISSION_ACTIONS.EDIT}`,
  PROGRAM_ASSIGNMENT_VIEW: `${FEATURES.PROGRAM_ASSIGNMENT}.${PERMISSION_ACTIONS.VIEW}`,
  PROGRAM_ASSIGNMENT_DELETE: `${FEATURES.PROGRAM_ASSIGNMENT}.${PERMISSION_ACTIONS.DELETE}`,

  // Frequency Configuration
  FREQUENCY_CONFIGURATION_ADD: `${FEATURES.FREQUENCY_CONFIGURATION}.${PERMISSION_ACTIONS.ADD}`,
  FREQUENCY_CONFIGURATION_EDIT: `${FEATURES.FREQUENCY_CONFIGURATION}.${PERMISSION_ACTIONS.EDIT}`,
  FREQUENCY_CONFIGURATION_VIEW: `${FEATURES.FREQUENCY_CONFIGURATION}.${PERMISSION_ACTIONS.VIEW}`,
  FREQUENCY_CONFIGURATION_DELETE: `${FEATURES.FREQUENCY_CONFIGURATION}.${PERMISSION_ACTIONS.DELETE}`,

  // Service Definition
  SERVICE_DEFINITION_ADD: `${FEATURES.SERVICE_DEFINITION}.${PERMISSION_ACTIONS.ADD}`,
  SERVICE_DEFINITION_EDIT: `${FEATURES.SERVICE_DEFINITION}.${PERMISSION_ACTIONS.EDIT}`,
  SERVICE_DEFINITION_VIEW: `${FEATURES.SERVICE_DEFINITION}.${PERMISSION_ACTIONS.VIEW}`,
  SERVICE_DEFINITION_DELETE: `${FEATURES.SERVICE_DEFINITION}.${PERMISSION_ACTIONS.DELETE}`,

  // Action Centre
  ACTION_CENTRE_ADD: `${FEATURES.ACTION_CENTRE}.${PERMISSION_ACTIONS.ADD}`,
  ACTION_CENTRE_EDIT: `${FEATURES.ACTION_CENTRE}.${PERMISSION_ACTIONS.EDIT}`,
  ACTION_CENTRE_VIEW: `${FEATURES.ACTION_CENTRE}.${PERMISSION_ACTIONS.VIEW}`,
  ACTION_CENTRE_DELETE: `${FEATURES.ACTION_CENTRE}.${PERMISSION_ACTIONS.DELETE}`,

  // Overview / Dashboard
  OVERVIEW_ADD: `${FEATURES.OVERVIEW}.${PERMISSION_ACTIONS.ADD}`,
  OVERVIEW_EDIT: `${FEATURES.OVERVIEW}.${PERMISSION_ACTIONS.EDIT}`,
  OVERVIEW_VIEW: `${FEATURES.OVERVIEW}.${PERMISSION_ACTIONS.VIEW}`,
  OVERVIEW_DELETE: `${FEATURES.OVERVIEW}.${PERMISSION_ACTIONS.DELETE}`,

  // Scenarios
  SCENARIOS_ADD: `${FEATURES.SCENARIOS}.${PERMISSION_ACTIONS.ADD}`,
  SCENARIOS_EDIT: `${FEATURES.SCENARIOS}.${PERMISSION_ACTIONS.EDIT}`,
  SCENARIOS_VIEW: `${FEATURES.SCENARIOS}.${PERMISSION_ACTIONS.VIEW}`,
  SCENARIOS_DELETE: `${FEATURES.SCENARIOS}.${PERMISSION_ACTIONS.DELETE}`,

  // Locations
  LOCATIONS_ADD: `${FEATURES.LOCATIONS}.${PERMISSION_ACTIONS.ADD}`,
  LOCATIONS_EDIT: `${FEATURES.LOCATIONS}.${PERMISSION_ACTIONS.EDIT}`,
  LOCATIONS_VIEW: `${FEATURES.LOCATIONS}.${PERMISSION_ACTIONS.VIEW}`,
  LOCATIONS_DELETE: `${FEATURES.LOCATIONS}.${PERMISSION_ACTIONS.DELETE}`,

  // Opening and Closing
  OPENING_AND_CLOSING_ADD: `${FEATURES.OPENING_AND_CLOSING}.${PERMISSION_ACTIONS.ADD}`,
  OPENING_AND_CLOSING_EDIT: `${FEATURES.OPENING_AND_CLOSING}.${PERMISSION_ACTIONS.EDIT}`,
  OPENING_AND_CLOSING_VIEW: `${FEATURES.OPENING_AND_CLOSING}.${PERMISSION_ACTIONS.VIEW}`,
  OPENING_AND_CLOSING_DELETE: `${FEATURES.OPENING_AND_CLOSING}.${PERMISSION_ACTIONS.DELETE}`,

  // Access Control
  ACCESS_CONTROL_ADD: `${FEATURES.ACCESS_CONTROL}.${PERMISSION_ACTIONS.ADD}`,
  ACCESS_CONTROL_EDIT: `${FEATURES.ACCESS_CONTROL}.${PERMISSION_ACTIONS.EDIT}`,
  ACCESS_CONTROL_VIEW: `${FEATURES.ACCESS_CONTROL}.${PERMISSION_ACTIONS.VIEW}`,
  ACCESS_CONTROL_DELETE: `${FEATURES.ACCESS_CONTROL}.${PERMISSION_ACTIONS.DELETE}`,

  // User Management
  USER_MANAGEMENT_ADD: `${FEATURES.USER_MANAGEMENT}.${PERMISSION_ACTIONS.ADD}`,
  USER_MANAGEMENT_EDIT: `${FEATURES.USER_MANAGEMENT}.${PERMISSION_ACTIONS.EDIT}`,
  USER_MANAGEMENT_VIEW: `${FEATURES.USER_MANAGEMENT}.${PERMISSION_ACTIONS.VIEW}`,
  USER_MANAGEMENT_DELETE: `${FEATURES.USER_MANAGEMENT}.${PERMISSION_ACTIONS.DELETE}`,
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ===========================================
// ROLES
// ===========================================

/**
 * User roles in the system
 */
export const ROLES = {
  ADMIN: 'Admin',
  PROGRAM_TEAM: 'ProgramTeam',
  READ_ONLY: 'ReadOnly',
  APPLICATION_SUPPORT: 'ApplicationSupport',
  DEFAULT_ROLE: 'DefaultRole',
  NEW_USER: 'NewUser',
  VIEWER: 'Viewer',
  MANAGER: 'Manager',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// ===========================================
// ROUTE PERMISSIONS
// ===========================================

/**
 * Permissions required to access specific routes
 * Maps route paths to required permissions
 */
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  // Provider Management
  '/providers': [PERMISSIONS.PROVIDERS_VIEW],
  '/providers/add': [PERMISSIONS.PROVIDERS_ADD],
  '/providers/edit': [PERMISSIONS.PROVIDERS_EDIT],

  // PM Services
  '/pm-services': [PERMISSIONS.PM_SERVICES_VIEW],
  '/pm-services/add': [PERMISSIONS.PM_SERVICES_ADD],
  '/pm-services/edit': [PERMISSIONS.PM_SERVICES_EDIT],

  // Work Orders
  '/work-orders': [PERMISSIONS.WORK_ORDERS_VIEW],

  // Program Assignment
  '/program-assignment': [PERMISSIONS.PROGRAM_ASSIGNMENT_VIEW],

  // Frequency Configuration
  '/frequency-configuration': [PERMISSIONS.FREQUENCY_CONFIGURATION_VIEW],

  // Service Definition
  '/service-definition': [PERMISSIONS.SERVICE_DEFINITION_VIEW],

  // Action Centre
  '/action-centre': [PERMISSIONS.ACTION_CENTRE_VIEW],

  // Overview / Dashboard
  '/overview': [PERMISSIONS.OVERVIEW_VIEW],
  '/dashboard': [PERMISSIONS.OVERVIEW_VIEW],

  // Scenarios
  '/scenarios': [PERMISSIONS.SCENARIOS_VIEW],
  '/scenarios/add': [PERMISSIONS.SCENARIOS_ADD],
  '/scenarios/edit': [PERMISSIONS.SCENARIOS_EDIT],

  // Locations
  '/locations': [PERMISSIONS.LOCATIONS_VIEW],

  // Opening and Closing
  '/opening-and-closing': [PERMISSIONS.OPENING_AND_CLOSING_VIEW],
  '/opening-closing-locations': [PERMISSIONS.OPENING_AND_CLOSING_VIEW],
};

// ===========================================
// FEATURE PERMISSIONS MATRIX
// ===========================================

/**
 * Feature permissions matrix
 * Defines which actions are available for each feature
 */
export const FEATURE_PERMISSIONS: Record<Feature, string[]> = {
  [FEATURES.PROVIDERS]: [
    PERMISSIONS.PROVIDERS_ADD,
    PERMISSIONS.PROVIDERS_EDIT,
    PERMISSIONS.PROVIDERS_VIEW,
    PERMISSIONS.PROVIDERS_DELETE,
  ],
  [FEATURES.PM_SERVICES]: [
    PERMISSIONS.PM_SERVICES_ADD,
    PERMISSIONS.PM_SERVICES_EDIT,
    PERMISSIONS.PM_SERVICES_VIEW,
    PERMISSIONS.PM_SERVICES_DELETE,
  ],
  [FEATURES.WORK_ORDERS]: [
    PERMISSIONS.WORK_ORDERS_ADD,
    PERMISSIONS.WORK_ORDERS_EDIT,
    PERMISSIONS.WORK_ORDERS_VIEW,
    PERMISSIONS.WORK_ORDERS_DELETE,
  ],
  [FEATURES.PROGRAM_ASSIGNMENT]: [
    PERMISSIONS.PROGRAM_ASSIGNMENT_ADD,
    PERMISSIONS.PROGRAM_ASSIGNMENT_EDIT,
    PERMISSIONS.PROGRAM_ASSIGNMENT_VIEW,
    PERMISSIONS.PROGRAM_ASSIGNMENT_DELETE,
  ],
  [FEATURES.FREQUENCY_CONFIGURATION]: [
    PERMISSIONS.FREQUENCY_CONFIGURATION_ADD,
    PERMISSIONS.FREQUENCY_CONFIGURATION_EDIT,
    PERMISSIONS.FREQUENCY_CONFIGURATION_VIEW,
    PERMISSIONS.FREQUENCY_CONFIGURATION_DELETE,
  ],
  [FEATURES.SERVICE_DEFINITION]: [
    PERMISSIONS.SERVICE_DEFINITION_ADD,
    PERMISSIONS.SERVICE_DEFINITION_EDIT,
    PERMISSIONS.SERVICE_DEFINITION_VIEW,
    PERMISSIONS.SERVICE_DEFINITION_DELETE,
  ],
  [FEATURES.ACTION_CENTRE]: [
    PERMISSIONS.ACTION_CENTRE_ADD,
    PERMISSIONS.ACTION_CENTRE_EDIT,
    PERMISSIONS.ACTION_CENTRE_VIEW,
    PERMISSIONS.ACTION_CENTRE_DELETE,
  ],
  [FEATURES.OVERVIEW]: [
    PERMISSIONS.OVERVIEW_ADD,
    PERMISSIONS.OVERVIEW_EDIT,
    PERMISSIONS.OVERVIEW_VIEW,
    PERMISSIONS.OVERVIEW_DELETE,
  ],
  [FEATURES.SCENARIOS]: [
    PERMISSIONS.SCENARIOS_ADD,
    PERMISSIONS.SCENARIOS_EDIT,
    PERMISSIONS.SCENARIOS_VIEW,
    PERMISSIONS.SCENARIOS_DELETE,
  ],
  [FEATURES.LOCATIONS]: [
    PERMISSIONS.LOCATIONS_ADD,
    PERMISSIONS.LOCATIONS_EDIT,
    PERMISSIONS.LOCATIONS_VIEW,
    PERMISSIONS.LOCATIONS_DELETE,
  ],
  [FEATURES.OPENING_AND_CLOSING]: [
    PERMISSIONS.OPENING_AND_CLOSING_ADD,
    PERMISSIONS.OPENING_AND_CLOSING_EDIT,
    PERMISSIONS.OPENING_AND_CLOSING_VIEW,
    PERMISSIONS.OPENING_AND_CLOSING_DELETE,
  ],
  [FEATURES.ACCESS_CONTROL]: [
    PERMISSIONS.ACCESS_CONTROL_ADD,
    PERMISSIONS.ACCESS_CONTROL_EDIT,
    PERMISSIONS.ACCESS_CONTROL_VIEW,
    PERMISSIONS.ACCESS_CONTROL_DELETE,
  ],
  [FEATURES.USER_MANAGEMENT]: [
    PERMISSIONS.USER_MANAGEMENT_ADD,
    PERMISSIONS.USER_MANAGEMENT_EDIT,
    PERMISSIONS.USER_MANAGEMENT_VIEW,
    PERMISSIONS.USER_MANAGEMENT_DELETE,
  ],
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Creates a permission string from feature and action
 * @param feature The feature name
 * @param action The action type
 * @returns The permission string in "feature.action" format
 */
export function createPermission(feature: Feature, action: PermissionAction): string {
  return `${feature}.${action}`;
}

/**
 * Parses a permission string into feature and action
 * @param permission The permission string (e.g., "providers.add")
 * @returns Object with feature and action, or null if invalid
 */
export function parsePermission(permission: string): { feature: string; action: string } | null {
  const parts = permission.split('.');
  if (parts.length !== 2) return null;

  return {
    feature: parts[0],
    action: parts[1],
  };
}

/**
 * Checks if a user has implicit view permission for a feature.
 * Users with add, edit, or delete permission implicitly have view permission.
 * @param userPermissions Array of user's permissions
 * @param permission The permission to check
 * @returns True if user has implicit view permission
 */
function hasImplicitViewPermission(userPermissions: string[], permission: string): boolean {
  if (!permission.endsWith('.view')) {
    return false;
  }
  const feature = permission.replace('.view', '');
  return (
    userPermissions.includes(`${feature}.add`) ||
    userPermissions.includes(`${feature}.edit`) ||
    userPermissions.includes(`${feature}.delete`)
  );
}

/**
 * Checks if a user has a specific permission
 * @param userPermissions Array of user's permissions
 * @param requiredPermission The permission to check
 * @returns True if user has the permission (explicit or implicit view)
 */
export function hasPermission(userPermissions: string[], requiredPermission: string): boolean {
  // Direct permission check
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }
  // Check implicit view permission
  return hasImplicitViewPermission(userPermissions, requiredPermission);
}

/**
 * Checks if a user has any of the specified permissions
 * @param userPermissions Array of user's permissions
 * @param requiredPermissions Array of permissions to check
 * @returns True if user has at least one of the permissions (explicit or implicit view)
 */
export function hasAnyPermission(
  userPermissions: string[],
  requiredPermissions: string[]
): boolean {
  return requiredPermissions.some(permission => hasPermission(userPermissions, permission));
}

/**
 * Checks if a user has all of the specified permissions
 * @param userPermissions Array of user's permissions
 * @param requiredPermissions Array of permissions to check
 * @returns True if user has all the permissions (explicit or implicit view)
 */
export function hasAllPermissions(
  userPermissions: string[],
  requiredPermissions: string[]
): boolean {
  return requiredPermissions.every(permission => hasPermission(userPermissions, permission));
}

/**
 * Gets all permissions for a specific feature
 * @param userPermissions Array of user's permissions
 * @param feature The feature to check
 * @returns Array of actions the user can perform on the feature
 */
export function getFeaturePermissions(userPermissions: string[], feature: Feature): string[] {
  return userPermissions.filter(permission => permission.startsWith(`${feature}.`));
}

/**
 * Checks if user can perform a specific action on a feature
 * @param userPermissions Array of user's permissions
 * @param feature The feature name
 * @param action The action to check
 * @returns True if user can perform the action
 */
export function canPerformAction(
  userPermissions: string[],
  feature: Feature,
  action: PermissionAction
): boolean {
  const permission = createPermission(feature, action);
  return hasPermission(userPermissions, permission);
}
